import { useState } from "react";
import { useGetList, useTimeout } from "ra-core";
import { Clock, ListChecks, Video } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

import type { Contact, ContactNote } from "../types";
import { DashboardActivityLog } from "./DashboardActivityLog";
import { DashboardActionItems } from "./DashboardActionItems";
import { DashboardStepper } from "./DashboardStepper";
import { RecentClientCalls } from "./RecentClientCalls";
import MobileHeader from "../layout/MobileHeader";
import { MobileContent } from "../layout/MobileContent";
import { useConfigurationContext } from "../root/ConfigurationContext";
import { usePendingContactApprovalsCount } from "../contacts/usePendingContactApprovalsCount";

const DashboardHeader = () => {
  const { darkModeLogo, lightModeLogo, title } = useConfigurationContext();
  return (
    <MobileHeader>
      <div className="flex items-center gap-2 text-secondary-foreground no-underline py-3">
        <img className="[.light_&]:hidden h-6" src={darkModeLogo} alt={title} />
        <img className="[.dark_&]:hidden h-6" src={lightModeLogo} alt={title} />
        <h1 className="text-xl font-semibold">{title}</h1>
      </div>
    </MobileHeader>
  );
};

const Wrapper = ({ children }: { children: React.ReactNode }) => {
  return (
    <>
      <DashboardHeader />
      <MobileContent>{children}</MobileContent>
    </>
  );
};

const Loading = () => (
  <Wrapper>
    <Skeleton className="h-4 w-3/4 mb-4" />
    <Skeleton className="h-4 w-full mb-2" />
    <Skeleton className="h-4 w-full mb-2" />
    <Skeleton className="h-4 w-full mb-2" />
    <Skeleton className="h-4 w-full mb-2" />
  </Wrapper>
);

type DashboardTab = "activity" | "calls" | "actions";

const TABS: { id: DashboardTab; icon: typeof Clock; label: string }[] = [
  { id: "activity", icon: Clock, label: "Activity" },
  { id: "calls", icon: Video, label: "Calls" },
  { id: "actions", icon: ListChecks, label: "Actions" },
];

export const MobileDashboard = () => {
  const {
    data: dataContact,
    total: totalContact,
    isPending: isPendingContact,
  } = useGetList<Contact>("contacts", {
    pagination: { page: 1, perPage: 1 },
  });
  const { total: totalContactNotes, isPending: isPendingContactNotes } =
    useGetList<ContactNote>("contact_notes", {
      pagination: { page: 1, perPage: 1 },
    });
  const oneSecondHasPassed = useTimeout(1000);

  const isPending = isPendingContact || isPendingContactNotes;

  if (isPending) {
    return oneSecondHasPassed ? <Loading /> : null;
  }

  if (!totalContact) {
    return (
      <Wrapper>
        <DashboardStepper step={1} />
      </Wrapper>
    );
  }

  if (!totalContactNotes) {
    return (
      <Wrapper>
        <DashboardStepper step={2} contactId={dataContact?.[0]?.id} />
      </Wrapper>
    );
  }

  return <MobileDashboardTabs />;
};

const MobileDashboardTabs = () => {
  const [activeTab, setActiveTab] = useState<DashboardTab>("activity");
  const pendingApprovals = usePendingContactApprovalsCount();

  return (
    <>
      <DashboardHeader />
      <main className="mx-auto max-w-screen-xl pt-18 px-4 pb-20 h-dvh overflow-hidden flex flex-col">
        {/* Tab switcher */}
        <div className="flex shrink-0 border-b" role="tablist">
          {TABS.map((tab) => {
            const isActive = activeTab === tab.id;
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                role="tab"
                aria-selected={isActive}
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  "relative flex flex-1 items-center justify-center gap-1.5 px-2 py-2.5 text-sm font-medium transition-colors",
                  isActive
                    ? "text-foreground"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                <Icon className="size-4" />
                <span className="hidden xs:inline">{tab.label}</span>
                {isActive && (
                  <span className="absolute bottom-0 left-1/4 right-1/4 h-0.5 rounded-full bg-primary" />
                )}
                {/* Badge for pending approvals on Activity tab */}
                {tab.id === "activity" && pendingApprovals > 0 && (
                  <Badge
                    variant="default"
                    className="ml-0.5 h-4.5 min-w-4.5 rounded-full px-1 text-[10px] leading-none"
                  >
                    {pendingApprovals}
                  </Badge>
                )}
              </button>
            );
          })}
        </div>

        {/* Active widget */}
        <div className="flex-1 min-h-0 mt-3">
          {activeTab === "activity" && <DashboardActivityLog />}
          {activeTab === "calls" && <RecentClientCalls linkable={false} />}
          {activeTab === "actions" && <DashboardActionItems />}
        </div>
      </main>
    </>
  );
};
